import { Button, Radio, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconMail } from '@tabler/icons-react';
import { SlackLogo } from '../slack/SlackLogo';
import NotificationsFormSlack from '../forms/notifications/NotificationsFormSlack';
import NotificationsFormEmail from '../forms/notifications/NotificationsFormEmail';
import { useSlack } from '~/hooks/slack/useSlack';
import { api } from '~/trpc-extra/api';
import { useCallback, useState } from 'react';
import type { NotificationProfile } from '@prisma/client';
import { useAutoAnimate } from '~/common/hooks/useAutoAnimate';

const NOTIFICATION_OPTIONS: Record<
  NotificationProfile,
  {
    label: string;
    description: string;
  }
> = {
  PROJECT: {
    label: 'Use project level settings',
    description: 'Use the project notification settings',
  },
  SUITE: {
    label: 'Use suite specific settings',
    description: 'Override the notification settings for this suite',
  },
  DISABLED: {
    label: 'Disable notifications',
    description: 'No notifications will be sent',
  },
};

export default function NotificationSettings({
  projectId,
  testSuiteId,
  selectedProfile,
  onProfileChange,
  hideChannelConfiguration = false,
}: {
  projectId: string;
  testSuiteId: string;
  selectedProfile: NotificationProfile;
  onProfileChange: (profile: NotificationProfile) => void;
  hideChannelConfiguration?: boolean;
}) {
  const { connectSlack, manageSlack } = useSlack();

  const { data: hasSlackInstalled } = api.project.hasSlackInstalled.useQuery(
    { projectId, testSuiteId },
    { enabled: !!projectId },
  );

  const { data: emailConfig, isLoading: isEmailLoading } = api.testSuite.getEmailConfig.useQuery(
    { projectId: projectId || '', testSuiteId },
    { enabled: !!projectId },
  );

  const { data: slackConfig, isLoading: isSlackLoading } = api.testSuite.getSlackConfig.useQuery(
    { projectId, testSuiteId },
    { enabled: !!projectId },
  );

  const hasEmailConfigured = !isEmailLoading && emailConfig;
  const hasSlackConfigured = !isSlackLoading && slackConfig;

  const [notificationProfile, setNotificationProfile] = useState<NotificationProfile>(selectedProfile);

  const updateNotificationProfile = useCallback(
    (profile: NotificationProfile) => {
      setNotificationProfile(profile);
      onProfileChange?.(profile);
    },
    [onProfileChange],
  );

  const [animationParent] = useAutoAnimate();

  return (
    <div className="flex w-full flex-col space-y-2">
      <div
        className="flex flex-col gap-8"
        ref={animationParent}
      >
        <Radio.Group
          value={notificationProfile}
          onChange={(value) => updateNotificationProfile(value as NotificationProfile)}
        >
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {Object.entries(NOTIFICATION_OPTIONS).map(([key, value]) => (
              <Radio.Card
                radius="md"
                value={key}
                key={key}
              >
                <div className="flex flex-row items-start gap-x-2">
                  <Radio.Indicator />
                  <div>
                    <Text
                      size="sm"
                      className="leading-[1.3] font-[var(--mantine-font-family-monospace)]"
                    >
                      {value.label}
                    </Text>
                    <Text
                      size="xs"
                      c="dimmed"
                      className="mt-1 text-[var(--mantine-color-dimmed)]"
                    >
                      {value.description}
                    </Text>
                  </div>
                </div>
              </Radio.Card>
            ))}
          </div>
        </Radio.Group>
        {notificationProfile === 'SUITE' && !hideChannelConfiguration && (
          <div className="flex flex-col gap-2">
            <div className="flex flex-row items-center gap-x-2 rounded-md border px-6 py-6 dark:border-gray-700">
              <IconMail size={20} />
              <div className="ml-3 flex flex-col">
                <div className="pr-2 text-sm font-bold text-stone-700 dark:text-stone-300">Email</div>
                <div className="text-xs whitespace-nowrap text-stone-500 dark:text-stone-400">
                  {hasEmailConfigured ? 'Notifications on' : 'No notifications configured'}
                </div>
              </div>
              <div className="flex w-full items-center justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    modals.open({
                      size: 'xl',
                      padding: '3rem',
                      radius: 'md',
                      centered: true,
                      withCloseButton: false,
                      children: (
                        <NotificationsFormEmail
                          projectId={projectId}
                          testSuiteId={testSuiteId}
                        />
                      ),
                    });
                  }}
                >
                  Configure
                </Button>
              </div>
            </div>
            <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-6 py-6 dark:border-gray-700">
              <SlackLogo size={20} />
              <div className="ml-3 flex flex-col">
                <div className="pr-2 text-sm font-bold text-stone-700 dark:text-stone-300">Slack</div>
                <div className="text-xs whitespace-nowrap text-stone-500 dark:text-stone-400">
                  {!hasSlackInstalled ?
                    'Not connected'
                  : !hasSlackConfigured ?
                    'No notifications configured'
                  : 'Notifications on'}
                </div>
              </div>
              <div className="flex w-full items-center justify-end">
                {hasSlackInstalled ?
                  <>
                    <Button
                      variant="filled"
                      className="mr-4"
                      onClick={async () => {
                        await manageSlack().catch(() => undefined);
                      }}
                    >
                      Manage Integration
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        modals.open({
                          size: 'xl',
                          padding: '3rem',
                          radius: 'md',
                          centered: true,
                          withCloseButton: false,
                          children: (
                            <NotificationsFormSlack
                              projectId={projectId}
                              testSuiteId={testSuiteId}
                            />
                          ),
                        });
                      }}
                    >
                      Configure
                    </Button>
                  </>
                : <Button
                    variant="outline"
                    onClick={async () => {
                      if (projectId && testSuiteId) {
                        await connectSlack(projectId, testSuiteId).catch(() => undefined);
                      }
                    }}
                  >
                    Connect
                  </Button>
                }
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
